import { useEffect, useRef, useState } from "react";
import { Camera, X } from "lucide-react";

/**
 * Leitor de código de barras / QR por câmera, via ZXing — funciona em qualquer
 * navegador (Chrome, Safari no iPhone/Mac, Firefox, Edge) e em qualquer câmera
 * (traseira, frontal, webcam do notebook, externa). Carregado sob demanda.
 *
 * props: onDetect(codigo) chamado quando lê um código; onClose() ao fechar.
 */
export default function BarcodeScanner({ onDetect, onClose }) {
  const videoRef = useRef(null);
  const detectRef = useRef(onDetect);
  detectRef.current = onDetect;
  const readerRef = useRef(null);
  const controlsRef = useRef(null);
  const [err, setErr] = useState("");
  const [cams, setCams] = useState([]);
  const [camId, setCamId] = useState(null);

  // (Re)inicia a decodificação numa câmera específica. Resolução alta melhora
  // muito a leitura de EAN-13 pequeno, curvado ou com brilho.
  const startOn = async (deviceId) => {
    try {
      if (controlsRef.current) { try { controlsRef.current.stop(); } catch { /* ignore */ } controlsRef.current = null; }
      setErr("");
      const video = deviceId
        ? { deviceId: { exact: deviceId }, width: { ideal: 1920 }, height: { ideal: 1080 } }
        : { facingMode: { ideal: "environment" }, width: { ideal: 1920 }, height: { ideal: 1080 } };
      controlsRef.current = await readerRef.current.decodeFromConstraints({ video }, videoRef.current, (result) => {
        if (result) detectRef.current(result.getText());
      });
    } catch {
      setErr("Não foi possível acessar esta câmera. Tente trocar de câmera ou digite o código.");
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [{ BrowserMultiFormatReader }, { DecodeHintType, BarcodeFormat }] = await Promise.all([
          import("@zxing/browser"), import("@zxing/library"),
        ]);
        if (cancelled) return;
        // TRY_HARDER + formatos de varejo => leitura muito mais confiável.
        const hints = new Map();
        hints.set(DecodeHintType.TRY_HARDER, true);
        hints.set(DecodeHintType.POSSIBLE_FORMATS, [
          BarcodeFormat.EAN_13, BarcodeFormat.EAN_8, BarcodeFormat.UPC_A, BarcodeFormat.UPC_E,
          BarcodeFormat.CODE_128, BarcodeFormat.CODE_39, BarcodeFormat.ITF, BarcodeFormat.QR_CODE,
        ]);
        readerRef.current = new BrowserMultiFormatReader(hints, { delayBetweenScanAttempts: 120 });
        let list = [];
        try { list = await BrowserMultiFormatReader.listVideoInputDevices(); } catch { /* segue com a câmera padrão */ }
        if (cancelled) return;
        setCams(list);
        const back = list.find((d) => /back|rear|traseira|environment/i.test(d.label));
        const initial = back?.deviceId ?? list[list.length - 1]?.deviceId ?? undefined;
        setCamId(initial ?? null);
        await startOn(initial);
      } catch {
        if (!cancelled) setErr("Não foi possível acessar a câmera. Permita o acesso no navegador ou digite o código.");
      }
    })();
    return () => { cancelled = true; try { controlsRef.current?.stop(); } catch { /* ignore */ } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const switchCam = () => {
    if (cams.length < 2) return;
    const idx = cams.findIndex((c) => c.deviceId === camId);
    const next = cams[(idx + 1) % cams.length];
    setCamId(next.deviceId);
    startOn(next.deviceId);
  };

  return (
    <div className="fixed inset-0 z-[60] bg-slate-900/90 flex flex-col items-center justify-center p-4" data-testid="barcode-scanner">
      <div className="w-full max-w-sm">
        {err ? (
          <p className="text-white text-sm text-center mb-4">{err}</p>
        ) : (
          <>
            <video ref={videoRef} aria-label="Pré-visualização da câmera para leitura do código de barras"
              className="w-full rounded-lg border-2 border-brand-500 bg-black aspect-[3/4] object-cover" muted playsInline autoPlay />
            <p className="text-white text-center text-sm mt-3">Aponte para o código de barras / QR</p>
          </>
        )}
        <div className="mt-4 flex gap-2">
          {cams.length > 1 && (
            <button onClick={switchCam} data-testid="scanner-switch" className="flex-1 bg-white/15 hover:bg-white/25 text-white font-semibold py-2.5 rounded-lg flex items-center justify-center gap-2"><Camera size={16} /> Trocar câmera</button>
          )}
          <button onClick={onClose} data-testid="scanner-close" className="flex-1 bg-white text-slate-800 font-semibold py-2.5 rounded-lg flex items-center justify-center gap-2"><X size={16} /> Fechar</button>
        </div>
      </div>
    </div>
  );
}
